import React, { useState, useMemo } from 'react';
import { FaExternalLinkAlt, FaGithub, FaMicrochip, FaSearch, FaYoutube } from 'react-icons/fa';
import './projects.scss';

// Importing assets for project GIFs
import codescribe from '../../assets/projects/codescribe_icon.png';
import cogniplay from '../../assets/projects/cogniplay.png';
import communityplate from '../../assets/projects/community.png';
import heartprediction from '../../assets/projects/heart.png';
import pomodoro_timer from '../../assets/projects/pomodoroimg.png';
import moneymgr from '../../assets/projects/moneymgr.png';
import clearalert from '../../assets/projects/clearalert.png';
import assemblyq from '../../assets/projects/assemblyq.png';

// Import projects data
import projectsData from '../../data/projects.json'; 

// Project interface for type safety
interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  featured: boolean;
  category: 'big' | 'small';
  technologies: {
    languages: string[];
    frameworks: string[];
    libraries: string[];
  };
  tags: string[];
  links: {
    github: string;
    live?: string | null;
    demo?: string | null;
    presentation?: string | null;
  };
}

// Asset mapping for project images
const assetMap: { [key: string]: string } = {
  'codescribe.png': codescribe,
  'moneymgr.png': moneymgr,
  'heart.png': heartprediction,
  'pomodoroimg.png': pomodoro_timer,
  'cogniplay.png': cogniplay,
  'community.png': communityplate,
  'clearalert.png': clearalert,
  'assemblyq.png': assemblyq,
};

const Projects: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [expandedTechProjects, setExpandedTechProjects] = useState<Set<string>>(new Set());

  // Get all unique tags from projects
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    (projectsData.projects as Project[]).forEach(project => {
      project.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, []);

  // Filter and order projects based on search term, selected tag, and featured toggle
  const filteredProjects = useMemo(() => {
    const allProjects = (projectsData.projects as Project[]);

    // Base filter: search + tag always apply across all projects
    let filtered = allProjects.filter(project => {
      const query = searchTerm.toLowerCase();
      const matchesSearch =
        project.title.toLowerCase().includes(query) ||
        project.description.toLowerCase().includes(query) ||
        project.technologies.languages.some(lang => lang.toLowerCase().includes(query)) ||
        project.technologies.frameworks.some(fw => fw.toLowerCase().includes(query)) ||
        project.technologies.libraries.some(lib => lib.toLowerCase().includes(query)) ||
        project.tags.some(tag => tag.toLowerCase().includes(query));

      const matchesTag = !selectedTag || project.tags.includes(selectedTag);

      return matchesSearch && matchesTag;
    });

    // When not showing all AND there is no active search or tag filter,
    // show only featured projects
    if (!showAll && !searchTerm && !selectedTag) {
      filtered = filtered.filter(p => p.featured);
    }

    // Always prioritize featured projects higher in the list
    filtered = filtered.sort((a, b) => {
      if (a.featured === b.featured) return 0;
      return a.featured ? -1 : 1;
    });

    return filtered;
  }, [searchTerm, selectedTag, showAll]);

  const bigProjects = filteredProjects.filter(project => project.category === 'big');
  const smallProjects = filteredProjects.filter(project => project.category === 'small');

  const toggleProjectTech = (projectId: string) => {
    setExpandedTechProjects(previousExpanded => {
      const nextExpanded = new Set(previousExpanded);
      if (nextExpanded.has(projectId)) {
        nextExpanded.delete(projectId);
      } else {
        nextExpanded.add(projectId);
      }
      return nextExpanded;
    });
  };

  const renderTechnologies = (project: Project, isSmall: boolean = false) => {
    const allTech = [
      ...project.technologies.languages,
      ...project.technologies.frameworks,
      ...project.technologies.libraries
    ];
    const isExpanded = expandedTechProjects.has(project.id);
    const visibleTech = isExpanded ? allTech : allTech.slice(0, isSmall ? 4 : 5);
    const hiddenTechCount = allTech.length - visibleTech.length;

    return (
      <div className="project-technologies">
        <span className="tech-uses">Tech Stack</span>
        <div className="tech-items">
          {visibleTech.map((tech, index) => (
            <span key={index} className="tech-item">{tech}</span>
          ))}
          {(hiddenTechCount > 0 || isExpanded) && (
            <button
              type="button"
              className="tech-item tech-more"
              onClick={() => toggleProjectTech(project.id)}
            >
              {isExpanded ? 'Show less' : `+${hiddenTechCount} more`}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderProject = (project: Project, isSmall: boolean = false) => {
  const imageSrc = assetMap[project.image];
  const containerClass = isSmall ? 'small-project' : 'project-container';
  

  return (
    <div key={project.id} className={containerClass}>
      <div className="project-image-wrapper">
        {imageSrc ? (
          <img src={imageSrc} alt={`${project.title} Project`} />
        ) : (
          <div className="project-image-fallback" aria-label={`${project.title} Project`}>
            <FaMicrochip className="fallback-icon" />
            <span className="fallback-title">{project.title}</span>
            <span className="fallback-subtitle">See it. Check it. Assemble it right.</span>
          </div>
        )}
      </div>
      <div className="project-content">
        <div className="project-header">
          <h3>{project.title}</h3>
          <div className="project-tags">
            {project.tags.map((tag, index) => (
              <span key={index} className="tag">{tag}</span>
            ))}
          </div>
        </div>
        <p className="project-description">{project.description}</p>
        
        {renderTechnologies(project, isSmall)}
        
        <div className="project-links">
          <a href={project.links.github} target="_blank" rel="noopener noreferrer">
            <FaGithub /> View Project
          </a>
          {project.links.live && (
            <a href={project.links.live} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt /> View Live
            </a>
          )}
          {project.links.demo && (
            <a href={project.links.demo} target="_blank" rel="noopener noreferrer">
              <FaYoutube /> View Demo
            </a>
          )}
          {project.links.presentation && (
            <a href={project.links.presentation} target="_blank" rel="noopener noreferrer">
              <FaExternalLinkAlt /> View Presentation
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

  return (
    <div className="projects-container" id="projects">
      <h2 className="section-title">Projects</h2>
      
      <div className="projects-controls">
        <div className="search-section">
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search projects, technologies, or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button 
            className={`show-all-button ${showAll ? 'active' : ''}`}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Featured' : 'Show All Projects'}
          </button>
        </div>
        
        <div className="filter-tags">
          <button
            className={`tag-filter ${!selectedTag ? 'active' : ''}`}
            onClick={() => setSelectedTag(null)}
          >
            All Tags
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              className={`tag-filter ${selectedTag === tag ? 'active' : ''}`}
              onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="projects-display">
        {filteredProjects.length === 0 ? (
          <div className="no-results">
            No projects found matching your search criteria.
          </div>
        ) : (
          <>
            {bigProjects.length > 0 && (
              <div className="big-projects-container">
                {bigProjects.map(project => renderProject(project, false))}
              </div>
            )}

            {smallProjects.length > 0 && (
              <div className="small-projects-container">
                {smallProjects.map(project => renderProject(project, true))}
                
                
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Projects;
